'use client';

import { useState, useEffect, useCallback, useRef } from 'react';

// ==========================================
// 1. MULTI-INSTANCE GLOBAL MEMORY
// ==========================================
const instances = {};

// ==========================================
// HELPER: Chuẩn hóa symbol cho từng market type
// ==========================================
const normalizeSymbol = (symbol, marketType) => {
  const raw = String(symbol || '').trim().toLowerCase();
  if (marketType === 'alpha') {
    if (raw.startsWith('alpha_')) return raw;
    return `alpha_${raw}`;
  }
  return raw;
};

const normalizeRestSymbol = (symbol, marketType) => {
  const raw = String(symbol || '').trim().toUpperCase();
  if (marketType === 'alpha') {
    if (raw.startsWith('ALPHA_')) return raw;
    return `ALPHA_${raw}`;
  }
  return raw;
};

const getInstance = (streamId) => {
  if (!instances[streamId]) {
    instances[streamId] = {
      globalWsAggTrade: null,
      globalWsDepth: null,
      globalWsDepthDiff250ms: null,
      currentConnectedSymbol: null,
      currentMarketType: 'futures',
      activeSubscribers: new Set(),
      isIntentionalClose: false,
      activeStreams: { aggTrade: [], depth: [] },
      activeStreamsDiff: { depthDiff: [] },
      localBookBufferDiff: { bids: {}, asks: {} },
      lastUpdateIdDiff: -1,
      diffQueue: [],
      diffUpdatePulse: 0,
      pulseListeners: new Set(),
      streamData: {
        price: 0,
        bids: [],
        asks: [],
        cvd: 0,
        bidsDiff: {},
        asksDiff: {}
      },
      wsStatus: { text: '⚪ CHƯA KẾT NỐI', color: '#94a3b8' },
      statusListeners: new Set()
    };
  }
  return instances[streamId];
};

const triggerPulse = (inst) => {
  inst.diffUpdatePulse = (inst.diffUpdatePulse + 1) % 100000;
  inst.pulseListeners.forEach((listener) => listener(inst.diffUpdatePulse));
};

const updateStatus = (inst, text, color) => {
  inst.wsStatus = { text, color };
  inst.statusListeners.forEach((listener) => listener(inst.wsStatus));
};

const applyDeltaEngine = (currentLevels, deltas) => {
  if (!deltas || !Array.isArray(deltas)) return;
  deltas.forEach(([priceStr, volStr]) => {
    const vol = parseFloat(volStr);
    if (vol === 0) {
      delete currentLevels[priceStr];
    } else {
      currentLevels[priceStr] = vol;
    }
  });
};

// ==========================================
// SNAPSHOT FETCHER (hỗ trợ cả Futures, Spot, Alpha)
// ==========================================
const fetchSnapshotForDiff = async (symbolRaw, marketType, inst) => {
  try {
    const normalizedLc = normalizeSymbol(symbolRaw, marketType);
    let snapshot = null;

    if (marketType === 'alpha') {
      const restSymbol = normalizeRestSymbol(symbolRaw, marketType);
      // BAPI endpoint của Binance Alpha cho orderbook depth
      const alphaUrl = `https://www.binance.com/bapi/defi/v1/public/wallet-direct/buw/wallet/cex/alpha/orderbook/depth?symbol=${restSymbol}&limit=100`;
      const res = await fetch(alphaUrl);
      const json = await res.json();
      const payload = json?.data || json;

      if (payload?.bids && payload?.asks) {
        snapshot = {
          bids: payload.bids,
          asks: payload.asks,
          lastUpdateId: payload.lastUpdateId ?? 0
        };
      } else {
        throw new Error('Alpha REST snapshot không có bids/asks');
      }
    } else if (marketType === 'spot') {
      const restSymbol = normalizeRestSymbol(symbolRaw, marketType);
      const res = await fetch(
        `https://api.binance.com/api/v3/depth?symbol=${restSymbol}&limit=1000`
      );
      snapshot = await res.json();
    } else {
      const restSymbol = normalizeRestSymbol(symbolRaw, marketType);
      const res = await fetch(
        `https://fapi.binance.com/fapi/v1/depth?symbol=${restSymbol}&limit=1000`
      );
      snapshot = await res.json();
    }

    if (inst.currentConnectedSymbol !== normalizedLc) return;

    const initialBids = {};
    const initialAsks = {};

    (snapshot.bids || []).forEach(([p, v]) => {
      if (parseFloat(v) > 0) initialBids[p] = parseFloat(v);
    });
    (snapshot.asks || []).forEach(([p, v]) => {
      if (parseFloat(v) > 0) initialAsks[p] = parseFloat(v);
    });

    inst.localBookBufferDiff = { bids: initialBids, asks: initialAsks };

    // Áp dụng queue đã buffer trong lúc chờ REST
    inst.diffQueue.forEach((data) => {
      if (data.u > snapshot.lastUpdateId) {
        if (data.b) applyDeltaEngine(inst.localBookBufferDiff.bids, data.b);
        if (data.a) applyDeltaEngine(inst.localBookBufferDiff.asks, data.a);
      }
    });

    inst.diffQueue = [];
    inst.lastUpdateIdDiff = snapshot.lastUpdateId;

    inst.streamData.bidsDiff = inst.localBookBufferDiff.bids;
    inst.streamData.asksDiff = inst.localBookBufferDiff.asks;

    updateStatus(inst, '🟢 LIVE (ĐÃ KẾT NỐI)', '#22c55e');
    triggerPulse(inst);
  } catch (err) {
    console.error('Lỗi lấy Snapshot luồng Diff:', err);
    // Fallback: vẫn cho phép WebSocket stream hoạt động
    updateStatus(inst, '🟡 LIVE (CHỈ WEBSOCKET)', '#f59e0b');
    inst.lastUpdateIdDiff = 0;
    triggerPulse(inst);
  }
};

const unsubscribeAndClose = (inst) => {
  inst.isIntentionalClose = true;

  if (inst.globalWsAggTrade) {
    if (inst.globalWsAggTrade.readyState === WebSocket.OPEN) {
      try {
        inst.globalWsAggTrade.send(
          JSON.stringify({ method: 'UNSUBSCRIBE', params: inst.activeStreams.aggTrade, id: 999 })
        );
      } catch (e) {}
      inst.globalWsAggTrade.close();
    }
    inst.globalWsAggTrade.onmessage = null;
  }

  if (inst.globalWsDepth) {
    if (inst.globalWsDepth.readyState === WebSocket.OPEN) {
      try {
        inst.globalWsDepth.send(
          JSON.stringify({ method: 'UNSUBSCRIBE', params: inst.activeStreams.depth, id: 999 })
        );
      } catch (e) {}
      inst.globalWsDepth.close();
    }
    inst.globalWsDepth.onmessage = null;
  }

  if (inst.globalWsDepthDiff250ms) {
    if (inst.globalWsDepthDiff250ms.readyState === WebSocket.OPEN) {
      try {
        inst.globalWsDepthDiff250ms.send(
          JSON.stringify({ method: 'UNSUBSCRIBE', params: inst.activeStreamsDiff.depthDiff, id: 888 })
        );
      } catch (e) {}
      inst.globalWsDepthDiff250ms.close();
    }
    inst.globalWsDepthDiff250ms.onmessage = null;
  }

  inst.globalWsAggTrade = null;
  inst.globalWsDepth = null;
  inst.globalWsDepthDiff250ms = null;
  inst.currentConnectedSymbol = null;

  inst.streamData.price = 0;
  inst.streamData.bids = [];
  inst.streamData.asks = [];
  inst.streamData.cvd = 0;

  inst.localBookBufferDiff = { bids: {}, asks: {} };
  inst.diffQueue = [];
  inst.lastUpdateIdDiff = -1;
  inst.streamData.bidsDiff = {};
  inst.streamData.asksDiff = {};
  triggerPulse(inst);

  updateStatus(inst, '⚪ ĐÃ NGẮT KẾT NỐI', '#94a3b8');
};

// ==========================================
// CONNECT TO BINANCE (Futures / Spot / Alpha)
// ==========================================
const connectToBinance = (symbol, marketType = 'futures', inst) => {
  const symbolLc = normalizeSymbol(symbol, marketType);

  if (
    inst.currentConnectedSymbol === symbolLc &&
    inst.currentMarketType === marketType &&
    inst.globalWsAggTrade &&
    inst.globalWsDepth &&
    inst.globalWsDepthDiff250ms
  ) {
    return;
  }

  if (
    inst.currentConnectedSymbol &&
    (inst.currentConnectedSymbol !== symbolLc || inst.currentMarketType !== marketType)
  ) {
    unsubscribeAndClose(inst);
  }

  inst.isIntentionalClose = false;
  inst.currentConnectedSymbol = symbolLc;
  inst.currentMarketType = marketType;
  updateStatus(inst, '⏳ ĐANG KẾT NỐI...', '#f59e0b');

  inst.streamData.bids = [];
  inst.streamData.asks = [];

  fetchSnapshotForDiff(symbol, marketType, inst);

  // ====== CẤU HÌNH URL WEBSOCKET THEO MARKET TYPE ======
  let aggUrl = 'wss://fstream.binance.com/market/stream';
  let depthUrl = 'wss://fstream.binance.com/public/stream';

  if (marketType === 'spot') {
    aggUrl = 'wss://stream.binance.com:9443/stream';
    depthUrl = 'wss://stream.binance.com:9443/stream';
  } else if (marketType === 'alpha') {
    aggUrl = 'wss://nbstream.binance.com/w3w/wsa/stream';
    depthUrl = 'wss://nbstream.binance.com/w3w/wsa/stream';
  }

  const setupWS = (url, streams, streamKey, isDepthStream, isDiffMode = false) => {
    const ws = new WebSocket(url);

    if (isDiffMode) {
      inst.activeStreamsDiff[streamKey] = streams;
    } else {
      inst.activeStreams[streamKey] = streams;
    }

    ws.onopen = () => {
      ws.send(JSON.stringify({ method: 'GET_PROPERTY', params: ['combined'], id: 2 }));
      ws.send(
        JSON.stringify({ method: 'SUBSCRIBE', params: streams, id: isDiffMode ? 8 : 4 })
      );
    };

    ws.onmessage = (event) => {
      let payload;
      try {
        payload = JSON.parse(event.data);
      } catch (e) {
        return;
      }

      // Xử lý response điều khiển
      if (payload.id === 2 && payload.result === false) {
        ws.send(JSON.stringify({ method: 'SET_PROPERTY', params: ['combined', true], id: 5 }));
        return;
      }
      if ((payload.id === 4 || payload.id === 8) && payload.result === null) {
        if (!isDiffMode) updateStatus(inst, '🟢 LIVE (ĐÃ KẾT NỐI)', '#22c55e');
        return;
      }
      if ((payload.id === 999 || payload.id === 888) && payload.result === null) return;

      const data = payload.data ? payload.data : payload;
      const eventType = data.e || payload.e;
      const bidsDelta = data.b || payload.b;
      const asksDelta = data.a || payload.a;

      if (
        isDiffMode &&
        isDepthStream &&
        (eventType === 'depthUpdate' || data.B || bidsDelta || asksDelta)
      ) {
        const uId = data.u || payload.u;
        const bD = data.b || payload.b;
        const aD = data.a || payload.a;

        if (inst.lastUpdateIdDiff === -1) {
          inst.diffQueue.push({ u: uId, b: bD, a: aD });
          return;
        }

        if (uId !== undefined && uId <= inst.lastUpdateIdDiff) return;

        if (bD) applyDeltaEngine(inst.localBookBufferDiff.bids, bD);
        if (aD) applyDeltaEngine(inst.localBookBufferDiff.asks, aD);

        inst.streamData.bidsDiff = inst.localBookBufferDiff.bids;
        inst.streamData.asksDiff = inst.localBookBufferDiff.asks;

        triggerPulse(inst);
      } else if (
        !isDiffMode &&
        isDepthStream &&
        (data.b || data.a || bidsDelta || asksDelta)
      ) {
        inst.streamData.bids = data.b || payload.b || [];
        inst.streamData.asks = data.a || payload.a || [];
      } else if (!isDiffMode && (data.e === 'aggTrade' || data.p)) {
        const priceVal = parseFloat(data.p);
        const qtyVal = parseFloat(data.q);
        inst.streamData.price = priceVal;
        if (data.m) inst.streamData.cvd -= qtyVal;
        else inst.streamData.cvd += qtyVal;
      }
    };

    ws.onerror = (err) => {
      console.error(`[WS ERROR - ${marketType} - ${streamKey}]`, err);
      updateStatus(inst, '🔴 LỖI WEBSOCKET', '#ef4444');
    };

    return ws;
  };

  inst.globalWsAggTrade = setupWS(aggUrl, [`${symbolLc}@aggTrade`], 'aggTrade', false, false);
  inst.globalWsDepth = setupWS(depthUrl, [`${symbolLc}@depth20@100ms`], 'depth', true, false);
  inst.globalWsDepthDiff250ms = setupWS(
    depthUrl,
    [`${symbolLc}@depth@100ms`],
    'depthDiff',
    true,
    true
  );
};

// ==========================================
// HOOK
// ==========================================
export function useBinanceStream(symbol, isEnabled, marketType = 'futures', streamId = 'default') {
  const inst = getInstance(streamId);
  const [wsStatus, setWsStatus] = useState(inst.wsStatus);
  const [pulse, setPulse] = useState(inst.diffUpdatePulse);
  const subscriberIdRef = useRef(Math.random().toString(36).substring(2, 9));

  useEffect(() => {
    inst.statusListeners.add(setWsStatus);
    inst.pulseListeners.add(setPulse);
    setWsStatus(inst.wsStatus);
    return () => {
      inst.statusListeners.delete(setWsStatus);
      inst.pulseListeners.delete(setPulse);
    };
  }, [inst]);

  useEffect(() => {
    const subId = subscriberIdRef.current;
    if (!symbol) return;

    if (isEnabled) {
      inst.activeSubscribers.add(subId);
      const normalizedLc = normalizeSymbol(symbol, marketType);
      if (
        inst.activeSubscribers.size === 1 ||
        inst.currentConnectedSymbol !== normalizedLc ||
        inst.currentMarketType !== marketType
      ) {
        connectToBinance(symbol, marketType, inst);
      } else {
        setWsStatus(inst.wsStatus);
      }
    } else {
      inst.activeSubscribers.delete(subId);
      if (inst.activeSubscribers.size === 0) {
        unsubscribeAndClose(inst);
      }
    }

    return () => {
      inst.activeSubscribers.delete(subId);
      if (inst.activeSubscribers.size === 0) {
        unsubscribeAndClose(inst);
      }
    };
  }, [symbol, isEnabled, marketType, inst]);

  const resetCVD = useCallback(() => {
    inst.streamData.cvd = 0;
  }, [inst]);

  return {
    streamData: inst.streamData,
    wsStatus,
    diffPulse: pulse,
    resetCVD,
    reconnectSocket: () => {
      unsubscribeAndClose(inst);
      connectToBinance(symbol, marketType, inst);
    },
    requestServerSubList: () => {
      if (inst.globalWsAggTrade && inst.globalWsAggTrade.readyState === WebSocket.OPEN) {
        inst.globalWsAggTrade.send(JSON.stringify({ method: 'LIST_SUBSCRIPTIONS', id: 4 }));
      }
    }
  };
}
